import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  PatientState,
  loadOrbiAgentFlag,
  selectChatLockedForSession,
  selectOrbiAgentAvailable,
  startNewConversation,
} from '@patient-ui/patient-web/store';

import { LayoutService } from '../../../shared/services/layout.service';
import { OrbiThreadComponent } from '../orbi-thread/orbi-thread.component';

/**
 * Hosts the Orbi chat panel — the whole of it: availability gating, the panel's
 * open state, which view it shows (thread or conversation history), the
 * new-conversation action and the session lock (PCSA-13157/13158/13169/13170).
 *
 * It exists so a LAYOUT can mount the AI Agent with one element. That wiring used
 * to live in `PortalLayoutComponent`, which meant the agent existed only on
 * authenticated `/portal/**` pages — so a patient who followed the agent's own
 * Find-a-Bill card to Update Insurance (`/invoices/insurance?navigatedFrom=RU`,
 * a PUBLIC-layout route) watched it disappear (design review 08/20, "agent
 * missing from the Update Insurance page"). Duplicating the wiring into the
 * second layout would have left two copies to keep in step, so it moved here
 * instead and both layouts render this component.
 *
 * The entry POINT is not part of this component. Where it sits in the markup is
 * load-bearing — last child of `.main-content-wrapper`, after the router outlet
 * (PCSA-13440, and the global `router-outlet + *` rule) — while the panel is an
 * off-canvas overlay that must stay OUT of that wrapper. One component cannot
 * straddle that boundary, and the entry point needs no host wiring anyway: it
 * self-gates, self-places and opens the panel itself.
 *
 * Everything is gated on `selectOrbiAgentAvailable` (Split flag AND
 * identity-verified, PCSA-13452), which is also what keeps the agent off the
 * public layout's GUEST paths — landing, registration, guest bill pay: nobody
 * there is a verified patient, so nothing renders.
 */
@Component({
  selector: 'patient-ui-orbi-agent-host',
  templateUrl: './orbi-agent-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiAgentHostComponent implements OnInit, OnDestroy {
  /**
   * Whether the agent is available at all: the Split flag AND the patient's
   * identity verification (PCSA-13452), evaluated independently in the store
   * selector.
   */
  readonly orbiAgentAvailable$ = this.patientStore.select(
    selectOrbiAgentAvailable
  );
  /**
   * Whether a locking guardrail (EMERGENCY/HARM) has locked the AI Agent for the
   * session (PCSA-13170). Disables the panel's new-conversation + history header
   * actions and blocks opening other conversations while locked.
   */
  readonly orbiChatLocked$ = this.patientStore.select(
    selectChatLockedForSession
  );
  readonly orbiPanelOpen$ = this.layoutService.getOrbiPanelOpen();

  /**
   * Which Orbi panel view is shown: the chat thread or the conversation-history
   * list (PCSA-13169). Driven by LayoutService so BOTH the panel header icon AND
   * the entry-field history affordance (PCSA-13312) open the history view via
   * one shared source of truth. Kept as a local mirror (synced in ngOnInit) so
   * the template + `toggleOrbiHistory` can read the current value synchronously;
   * every write goes through `layoutService.setOrbiHistoryOpen`.
   */
  orbiHistoryOpen = false;

  /**
   * Local mirror of the session lock (PCSA-13170), synced in ngOnInit. Lets the
   * new-conversation guard below block synchronously while a locking guardrail
   * holds the session.
   */
  orbiChatLocked = false;

  /** The chat thread, so a resume can move focus into its input (AC 7d). */
  @ViewChild(OrbiThreadComponent) private orbiThread?: OrbiThreadComponent;

  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly patientStore: Store<PatientState>,
    private readonly layoutService: LayoutService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Re-evaluate the Orbi Split flag whenever a shell that can show the agent
    // mounts (covers refresh / session restore / a deep link straight into a
    // page). OrbiFeatureEffects also loads it on bootstrap (OnInitEffects) and on
    // authenticateSuccess; switchMap in the effect makes the overlapping triggers
    // idempotent.
    this.patientStore.dispatch(loadOrbiAgentFlag());
    // Mirror the shared Orbi history-view intent into the local flag so the
    // template + toggle read it synchronously. Both the panel header icon and
    // the entry-field history affordance (PCSA-13312) write it via LayoutService.
    this.layoutService
      .getOrbiHistoryOpen()
      .pipe(takeUntil(this.destroyed))
      .subscribe((open) => {
        this.orbiHistoryOpen = open;
        this.cdr.markForCheck();
      });
    // Mirror the session lock (PCSA-13170) so the new-conversation guard can
    // block synchronously. History stays openable while locked (mobile parity):
    // the list itself grays out + disables the other conversations, so there is
    // no need to force the user out of the history view when a lock latches.
    this.orbiChatLocked$.pipe(takeUntil(this.destroyed)).subscribe((locked) => {
      this.orbiChatLocked = locked;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  closeOrbiPanel(): void {
    this.layoutService.setOrbiHistoryOpen(false);
    this.layoutService.setOrbiPanelOpen(false);
  }

  /**
   * Panel header history icon (PCSA-13169): toggle between the chat thread and
   * the conversation-history view. There is no separate back arrow (per design)
   * — the header icon closes history the same way it opened it.
   */
  toggleOrbiHistory(): void {
    // Openable even while locked (PCSA-13170, mobile parity): the user CAN browse
    // history during a lock — but the list itself grays out + disables every
    // other conversation and the read-only one keeps its Read-Only pill, so the
    // lock still cannot be bypassed by resuming another chat (see the history
    // list's `sessionLocked` handling). Mobile: `orbi_history_drawer.dart`.
    this.layoutService.setOrbiHistoryOpen(!this.orbiHistoryOpen);
  }

  /**
   * A resumed conversation returns to the thread. Once the thread view is back
   * on screen, move focus into its input so the user can continue the
   * conversation immediately (PCSA-13169 AC 7d). The `*ngIf` swap + OnPush view
   * must flush first, hence the microtask defer + explicit detectChanges.
   */
  closeOrbiHistory(): void {
    this.layoutService.setOrbiHistoryOpen(false);
    this.cdr.detectChanges();
    setTimeout(() => this.orbiThread?.focusInput());
  }

  /** Panel "+" header action: start a fresh conversation (PCSA-13158). */
  newOrbiConversation(): void {
    // Blocked while locked (PCSA-13170): a locking guardrail makes the AI Agent
    // unavailable for the session, so no new conversation can be started.
    if (this.orbiChatLocked) {
      return;
    }
    this.layoutService.setOrbiHistoryOpen(false);
    this.patientStore.dispatch(startNewConversation());
  }
}
